import "core-js/modules/es.date.to-string.js";
/* eslint-disable no-param-reassign */
import { differenceInYears, parse as dateParser } from 'date-fns';
import fi from 'date-fns/locale/fi/index';
import { FEMALE_NATIONAL_ID, MALE_NATIONAL_ID, INVALID, VALID } from './constants';
var NATIONAL_ID_REGEX = /(3[01]|[0-2]\d)(1[0-2]|0\d)\d{2}[A-](00[2-9]|[0-8][1-9]\d|[1-8]\d\d)[0-9ABCDEFHJKLMNPRSTUVWXY]$/;

// In FI, if the first digit of the last 4 digits is a 9,
// means it's a fake SSN for testing purposes or temporary usage.
var NATIONAL_ID_REGEX_INCLUDING_TEST_NATIONAL_ID = /(3[01]|[0-2]\d)(1[0-2]|0\d)\d{2}[A-](00[2-9]|[0-8][1-9]\d|[1-9]\d\d)[0-9ABCDEFHJKLMNPRSTUVWXY]$/;
var CONTROL_ARRAY = '0123456789ABCDEFHJKLMNPRSTUVWXYZ'.split('');
var LENGTH_REGEX = /^.{11}$/;
var NATIONAL_ID_WHITE_LIST = ['010200A9618', '291292-918R', '030883-925M', '170677-924F', '161188-959U', '211259-961E', '300960-943H', '130771-931C', '290157-948T', '170670-9993', '121186-9459', '180164-9397', '150971-9275', '160770-951P'];
var determineEnv = function determineEnv() {
  if (typeof process === 'undefined') return 'development';
  var APPLICATION_ID = process.env.SPIRIT_APPLICATION_ID || '';
  var APPLICATION_NAME = process.env.SPIRIT_APPLICATION_NAME || '';
  var groups = APPLICATION_ID.replace("".concat(APPLICATION_NAME, "-"), '').match(/^[a-z]{2}-(development|staging|production)$/);
  var env = groups ? groups[1] : 'unknown';
  return env;
};

/**
 * Returns the checksum for a finnish nationalId, e.g the
 * two last digits in the nationalId
 *
 * This function makes no guarantees on the return value
 * unless the nationalId is first sanitized.
 *
 * @param {string} sanitizedNationalId
 * @return {string} The checksum
 */
var getNationalIdChecksum = function getNationalIdChecksum(sanitizedNationalId) {
  var nationalId = sanitizedNationalId.substring(0, 10);
  var getIndex = function getIndex(id) {
    var number = '';

    // eslint-disable-next-line no-restricted-syntax
    for (var i in id) {
      if (!Number.isInteger(+id[i])) {
        // eslint-disable-next-line no-continue
        continue;
      }
      number += id[i];
    }
    return +number % 31;
  };
  var index = getIndex(nationalId).toString();
  return CONTROL_ARRAY[index];
};
var getAgeFields = function getAgeFields(birthDate) {
  var now = new Date();
  return {
    age: differenceInYears(now, birthDate),
    birthDate: birthDate.toISOString()
  };
};
var checkHasCorrectCenturyToken = function checkHasCorrectCenturyToken(birthDate, centuryToken) {
  var _getAgeFields = getAgeFields(birthDate),
    age = _getAgeFields.age;
  var hasCorrectCenturyToken = !(age > 100 && centuryToken === '-');
  return hasCorrectCenturyToken;
};
var createResult = function createResult(sanitized, result) {
  var epoch = new Date(0);
  var day = sanitized.substring(0, 2);
  var month = sanitized.substring(2, 4);
  var year = sanitized.substring(4, 6);
  var centuryToken = sanitized.substring(6, 7);
  var genderDigit = Number.parseInt(sanitized.charAt(9), 10);
  var birthday;
  if (centuryToken === '-') {
    birthday = "".concat(day).concat(month, "19").concat(year);
  } else if (centuryToken === 'A') {
    birthday = "".concat(day).concat(month, "20").concat(year);
  }
  var birthDate = dateParser(birthday, 'ddMMyyyy', epoch, {
    locale: fi
  });
  // eslint-disable-next-line no-restricted-globals
  if (isNaN(birthDate)) {
    return result;
  }
  var hasCorrectCenturyToken = checkHasCorrectCenturyToken(birthDate, centuryToken);
  if (!hasCorrectCenturyToken) {
    birthday = "".concat(day).concat(month, "20").concat(year);
    birthDate = dateParser(birthday, 'ddMMyyyy', epoch, {
      locale: fi
    });
    sanitized = sanitized.replace('-', 'A');
  }
  var ageFields = getAgeFields(birthDate);
  result.birthDate = ageFields.birthDate;
  result.gender = genderDigit % 2 === 0 ? FEMALE_NATIONAL_ID : MALE_NATIONAL_ID;
  result.age = ageFields.age;
  result.valid = VALID;
  result.nationalId = sanitized;
  result.error.type = null;
  result.error.message = null;
  return result;
};
var parse = function parse(nationalId, opts) {
  var result = {
    birthDate: null,
    gender: null,
    age: null,
    valid: INVALID,
    nationalId: null,
    error: {
      type: 'genericError',
      message: "Invalid national Id: ".concat(nationalId)
    }
  };
  if (!nationalId || !String(nationalId || '').trim()) {
    result.error.type = 'length';
    result.error.message = 'Empty invalid national Id';
    return result;
  }
  var trimmedNationalId = nationalId.trim();
  if (trimmedNationalId.length !== 11) {
    result.error.type = 'length';
    result.error.message = "Invalid national Id length: ".concat(nationalId);
    return result;
  }
  var env = determineEnv();

  // eslint-disable-next-line no-useless-escape
  var sanitized = trimmedNationalId.toUpperCase().replace(/[\s\.]/g, '');
  if ((env === 'staging' || env === 'development') && NATIONAL_ID_WHITE_LIST.includes(sanitized)) {
    return createResult(sanitized, result);
  }
  var nationalIdRegex = opts && opts.allowTestNationalId ? NATIONAL_ID_REGEX_INCLUDING_TEST_NATIONAL_ID : NATIONAL_ID_REGEX;
  if (!nationalIdRegex.test(sanitized) || !LENGTH_REGEX.test(sanitized)) {
    return result;
  }
  var checksum = getNationalIdChecksum(sanitized);
  if (checksum !== sanitized.substr(-1)) {
    return result;
  }
  return createResult(sanitized, result);
};
export default parse;
export { NATIONAL_ID_WHITE_LIST };